import { useEffect } from "react";
import { enums } from "@shared/constants";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";

// Extend the window object to include grecaptcha
declare global {
  interface Window {
    grecaptcha: ReCaptchaV3;
  }
}

// Define the type for the reCAPTCHA v3 object
interface ReCaptchaV3 {
  execute(siteKey: string, options?: ReCaptchaExecuteOptions): Promise<string>;
  ready(callback: () => void): void;
}

interface ReCaptchaExecuteOptions {
  action?: string;
}

// Define the type for the hook return value
interface UseRecaptcha {
  executeRecaptcha: (action: string) => Promise<string | undefined>; // returns token value
}

const RECAPTCHA_V3_SITE_KEY = enums.RECAPTCHA_KEY_MAP.PUBLIC_KEY.key_value;

/**
 * A hook to load and execute Google's reCAPTCHA v3
 * @param enforceRecaptcha - If true, the recaptcha will be enforced even if the feature flag is not enabled
 * Param is temporary to enforce recaptcha for the contact us form, and to be removed
 * once the recaptcha is complete for the login form
 */
export default function useRecaptcha(enforceRecaptcha = false): UseRecaptcha {
  const { SWITCHES } = useDjangoWaffle();

  useEffect(() => {
    if (SWITCHES.enable_captcha || enforceRecaptcha) {
      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_V3_SITE_KEY}`;
      document.body.appendChild(script);
    }
  }, [SWITCHES.enable_captcha]);

  const waitForRecaptcha = (): Promise<ReCaptchaV3> =>
    new Promise((resolve) => {
      if (typeof window !== "undefined" && window.grecaptcha) {
        resolve(window.grecaptcha);
      } else {
        const checkInterval = setInterval(() => {
          if (typeof window !== "undefined" && window.grecaptcha) {
            clearInterval(checkInterval);
            resolve(window.grecaptcha);
          }
        }, 100);
      }
    });

  /**
   * Wait for reCAPTCHA to load and execute the reCAPTCHA v3 action
   */
  const executeRecaptcha = async (
    action: string
  ): Promise<string | undefined> => {
    if (!SWITCHES.enable_captcha && !enforceRecaptcha) {
      return undefined;
    }
    try {
      const recaptcha = await waitForRecaptcha();
      return recaptcha.execute(RECAPTCHA_V3_SITE_KEY, {
        action,
      });
    } catch (error) {
      // handle error gracefully so that the original function can continue
      // eslint-disable-next-line no-console
      console.error("reCAPTCHA execution failed:", error);
      return undefined;
    }
  };

  return {
    executeRecaptcha,
  };
}
